/**
 * §A.1 · El dueño que todavía no ha entrado.
 *
 * La invitación del alta vale 14 días y una sola vez. Si caduca sin abrirse, un
 * enlace de acceso normal no le sirve: su membresía sigue pendiente y entraría en
 * Kiap **sin ver su club**. Este botón le manda un enlace nuevo
 * (`POST /admin/academies/{id}/owner-invitation`) y solo aparece mientras
 * `owner.pending` es cierto. 08-10-2026.
 *
 * Lleva `Idempotency-Key` nueva por pulsación, como el arranque: manda un correo, y
 * un doble clic no debe mandarle dos enlaces.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Error as PanelError } from '@/shared/ui/Estados';

export function InvitacionDelDueno({
  clubId,
  correo,
}: {
  clubId: string;
  correo: string;
}) {
  const cache = useQueryClient();
  const [enviada, setEnviada] = useState(false);
  const [problema, setProblema] = useState<Problema | null>(null);

  const reenviar = useMutation({
    mutationFn: async () => {
      const r = await api.v1.admin
        .academies({ id: clubId })
        ['owner-invitation'].post(
          {},
          { headers: { 'idempotency-key': crypto.randomUUID() } }
        );
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setProblema(null);
      setEnviada(true);
      void cache.invalidateQueries({ queryKey: ['admin', 'club', clubId] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  return (
    <div className="tenue">
      <span className="pastilla">Invitado, sin entrar</span>{' '}
      {enviada ? (
        <span role="status">
          Enlace nuevo enviado a {correo}. Vale 14 días.
        </span>
      ) : (
        <button
          type="button"
          className="btn-plano"
          disabled={reenviar.isPending}
          onClick={() => reenviar.mutate()}
        >
          {reenviar.isPending ? 'Enviando…' : 'Reenviar invitación'}
        </button>
      )}
      {problema && <PanelError problema={problema} />}
    </div>
  );
}
